import {
  extractErrorMessage,
  getApiQualityAiFeedbackV1EvidenceFeedbackRetrieveQueryKey,
  useApiQualityAiFeedbackV1EvidenceFeedbackCreate,
  useApiQualityAiFeedbackV1EvidenceFeedbackRetrieve,
  useApiQualityAiFeedbackV1FeedbackDestroy,
  useApiQualityAiFeedbackV1FeedbackUpdate,
  useQueryClient,
} from 'api';
import { useCallback, useMemo } from 'react';
import { useNotification } from '../../../notifications/hooks/useNotification/useNotification';

export const useSegmentationComments = (
  evidenceId: number,
  {
    enableFetch = false,
  }: {
    enableFetch?: boolean;
  } = {}
) => {
  const queryClient = useQueryClient();
  const { open: toast } = useNotification();

  const commentsQuery = useApiQualityAiFeedbackV1EvidenceFeedbackRetrieve(
    evidenceId,
    {},
    {
      query: {
        enabled: enableFetch,
      },
    }
  );

  const invalidateComments = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: getApiQualityAiFeedbackV1EvidenceFeedbackRetrieveQueryKey(evidenceId),
    });
  }, [queryClient, evidenceId]);

  const { mutate: deleteComment, isPending: isDeleting } = useApiQualityAiFeedbackV1FeedbackDestroy({
    mutation: {
      onError: (error) => {
        toast({
          description: extractErrorMessage(error),
          message: 'Error deleting comment',
          type: 'error',
        });
      },
      onSuccess: () => {
        invalidateComments();
      },
    },
  });

  const { mutate: createComment, isPending: isCreating } = useApiQualityAiFeedbackV1EvidenceFeedbackCreate({
    mutation: {
      onError: (error) => {
        toast({
          description: extractErrorMessage(error),
          message: 'Error adding comment',
          type: 'error',
        });
      },
      onSuccess: () => {
        invalidateComments();
      },
    },
  });

  const { mutate: updateComment, isPending: isUpdating } = useApiQualityAiFeedbackV1FeedbackUpdate({
    mutation: {
      onError: (error) => {
        toast({
          description: extractErrorMessage(error),
          message: 'Error updating comment',
          type: 'error',
        });
      },
      onSuccess: () => {
        invalidateComments();
      },
    },
  });

  const comments = useMemo(() => {
    if (!enableFetch) {
      return [];
    }

    return commentsQuery.data?.results ?? [];
  }, [commentsQuery.data, enableFetch]);

  return {
    comments,
    commentsError: commentsQuery.error,
    createComment,
    deleteComment,
    isCommentsError: enableFetch ? commentsQuery.isError : false,
    isCommentsFetching: enableFetch ? commentsQuery.isFetching : false,
    isCommentsLoading: enableFetch ? commentsQuery.isLoading : false,
    isCreating,
    isDeleting,
    isUpdating,
    refetchComments: commentsQuery.refetch,
    updateComment,
  };
};
